import appInfo from "@common/appInfo";
import { type GenericMap } from "@common/declarations";
import { Common } from "@common/types";
import { isFunction } from "lodash";
import React from "react";
import { labelize, toCamelCase, traverse } from "./misc";
import { capitalizeFirstLetter } from "./string";

export type I18n = GenericMap<string>;

let _i18n: I18n = {};

export const hasI18Key = (key: string) => key in _i18n;

export const initI18n = (common: I18n) => {
  Object.assign(
    _i18n,
    Object.keys(common).reduce((res, x) => {
      res[x] = replaceCommonTokens(common[x]);
      return res;
    }, {})
  );
};

const isAbbreviation = (text: string) => text.match(/^[A-Z0-9]+$/);

export const i18nFormatN = (n: number, singular: string, plural: string): string =>
  i18nFormat(n === 1 ? singular : plural, ["N", n]);

export const i18nFormat = (
  message: string,
  ...args: Array<[string, Common.Primitive | undefined | null, ((x: string) => string)?]>
): string =>
  args.reduce((res, p) => {
    const key = p[0];
    let value = (p[1] ?? "").toString();
    const transform = p[2];

    if (transform && !isAbbreviation(value)) {
      value = transform(value);
    }

    return res.replaceAll(`{${key}}`, value);
  }, message);

type Clickable = {
  caption?: string;
  onClick?: (e: React.MouseEvent) => void;
  keepCase?: boolean;
  tag?: string;
  className?: string;
};
type HtmlToken = [string, Common.Primitive | undefined | null | (() => any) | Clickable];

const i18nFormatHtmlLine = (line: string, ...args: Array<HtmlToken>): React.ReactElement[] => {
  const parts = line.split(/\{([^}]+)\}/gm);

  return parts.map((x) => {
    const token = args.find((y) => y[0] === x);
    if (token) {
      if (!token[1]) return null;
      if (isFunction(token[1])) return token[1]();
      if (typeof token[1] === "object" && "onClick" in token[1]) {
        const clickable = token[1] as Clickable;
        const Tag = (clickable.tag ?? "span") as unknown as React.ComponentType<React.HTMLProps<any>>;
        return (
          <Tag
            className={clickable.className}
            key={token[0]}
            onClick={clickable.onClick}
            style={{ textTransform: clickable.keepCase ? undefined : "lowercase" }}
          >
            {clickable.caption}
          </Tag>
        );
      }
      if (typeof token[1] === "string") {
        return <span key={token[0]} dangerouslySetInnerHTML={{ __html: token[1] }} />;
      }
      return token[1];
    }
    return x;
  });
};

export const i18nFormatHtml = (message: string, ...args: Array<HtmlToken>): React.ReactElement => {
  const lines = message.split("<br/>");
  return (
    <>
      {lines.length === 1
        ? i18nFormatHtmlLine(lines[0], ...args)
        : lines.map((x, i) => <div key={i}>{i18nFormatHtmlLine(x, ...args)}</div>)}
    </>
  );
};

const i18n = new Proxy(_i18n, {
  get(target: any, name: string | Symbol): string | undefined {
    if (typeof name !== "string") return; // TODO: figure out why can be a Symbol

    const ccName = toCamelCase(name);

    if (!(ccName in target)) return labelize(name);
    return target[ccName];
  }
});
export default i18n;

const replaceCommonTokens = (label: string): string => {
  label = label.replaceAll("{BRAND_TYPE}", appInfo.system.brandKey);

  return label;
};

export const localize = (i18n: I18n, data: object | any[], omit?: (name: string) => boolean) => {
  if (!Object.keys(i18n).length) return data;

  traverse(data, (obj, k) => {
    if (!obj[k] || (omit && omit(k))) return;
    if (k.toLocaleLowerCase().endsWith("label") && obj[k]) {
      obj[k] = replaceCommonTokens(i18n[obj[k]]);
    }
  });
};

export const i18nFormatters = {
  nDayOfMonth: (i18n: I18n, day: number) => {
    let suffix = day.toString().endsWith("1")
      ? "st"
      : day.toString().endsWith("2")
      ? "nd"
      : day.toString().endsWith("3")
      ? "rd"
      : "th";
    return i18nFormat(i18n.nDayOfMonth, ["N", `${day}${suffix}`]);
  }
};

export const merge = (extend: I18n | undefined): I18n => {
  if (!extend) return i18n;

  return new Proxy(extend, {
    get(target: any, name: string | Symbol): string {
      if (typeof name !== "string") {
        // TODO: figure out why sometimes its a Symbol
        return "!!!SYMBOL!!!";
      }
      const ccName = toCamelCase(name);

      // TODO_NEXT3

      if (ccName in target) return target[ccName];

      return `${capitalizeFirstLetter(name)} 🚫`;

      // if (!(ccName in i18n)) return labelize(name);
      // return i18n[ccName];
    }
  });
};
